import { useEffect } from 'react';
import { create } from 'zustand';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (tone, message) =>
    set((state) => ({
      // Cap the stack — a failed sync can otherwise produce a wall of toasts
      // that hides the app.
      toasts: [...state.toasts.slice(-3), { id: Math.random().toString(36).slice(2), tone, message }],
    })),
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) })),
}));

/** Imperative API, callable from mutation handlers outside React. */
export const toast = {
  success: (message: string) => useToastStore.getState().push('success', message),
  error: (message: string) => useToastStore.getState().push('error', message),
  info: (message: string) => useToastStore.getState().push('info', message),
};

const TONES: Record<ToastTone, { className: string; icon: JSX.Element }> = {
  success: {
    className: 'border-brand-200 bg-brand-50 text-brand-900',
    icon: <CheckCircle2 className="h-5 w-5 text-brand-600" />,
  },
  error: {
    className: 'border-red-200 bg-red-50 text-red-900',
    icon: <AlertCircle className="h-5 w-5 text-red-600" />,
  },
  info: {
    className: 'border-sky-200 bg-sky-50 text-sky-900',
    icon: <Info className="h-5 w-5 text-sky-600" />,
  },
};

function ToastItem({ toast: item }: { toast: Toast }): JSX.Element {
  const dismiss = useToastStore((state) => state.dismiss);

  useEffect(() => {
    // Errors stay longer: they usually carry something the user must read and
    // act on, not just an acknowledgement.
    const timer = setTimeout(() => dismiss(item.id), item.tone === 'error' ? 8000 : 4000);
    return () => clearTimeout(timer);
  }, [item.id, item.tone, dismiss]);

  const tone = TONES[item.tone];

  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg',
        tone.className,
      )}
    >
      <span className="shrink-0">{tone.icon}</span>
      <p className="flex-1 text-sm">{item.message}</p>
      <button
        type="button"
        onClick={() => dismiss(item.id)}
        className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function ToastContainer(): JSX.Element {
  const toasts = useToastStore((state) => state.toasts);

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col gap-2 sm:left-auto sm:right-4 sm:w-96 no-print">
      {toasts.map((item) => (
        <ToastItem key={item.id} toast={item} />
      ))}
    </div>
  );
}
